import { HeartHandshake, HardHat, Recycle, ArrowUpRight } from "lucide-react";
import { Reveal, SectionTag, scrollToSection } from "@/components/Reveal";

const PILLARS = [
  { icon: HeartHandshake, title: "Ethical Labour Practices", desc: "No child or forced labour. Wage records and working-hour practices form part of every supplier audit." },
  { icon: HardHat, title: "Safe & Compliant Facilities", desc: "Fire safety, sanitation, and structural and electrical compliance verified during factory visits." },
  { icon: Recycle, title: "Sustainable Material Streams", desc: "Access to organic, recycled and certified material options wherever the buyer's programme requires it." },
];

const MODELS = [
  {
    name: "Merchant Export",
    desc: "We buy from the factory and invoice you directly as the exporter of record.",
    points: ["One supplier, one invoice", "Consolidated shipments", "Simplified payment terms"],
  },
  {
    name: "Sourcing Help",
    desc: "We source, inspect and coordinate; you contract with and pay the factory directly.",
    points: ["Commission on Purchase Order", "Full pricing transparency", "Best for established importers"],
    featured: true,
  },
  {
    name: "Dedicated Sourcing Desk",
    desc: "A named team working exclusively on your categories, calendar and cost targets.",
    points: ["Monthly cost and KPI reviews", "Priority sampling capacity", "Retainer-based engagement"],
  },
];

export const Engagement = () => (
  <section id="engagement" className="relative py-28 lg:py-40 bg-[#F4F7FC]" data-testid="engagement-section">
    <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
      <Reveal>
        <SectionTag index="08" label="Responsible Sourcing" />
      </Reveal>

      <div className="grid lg:grid-cols-12 gap-12 mb-28">
        <Reveal className="lg:col-span-6">
          <blockquote className="font-display font-medium text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.2] text-[#0A1B33] tracking-tight" data-testid="responsible-quote">
            "We only place orders in factories we would be comfortable walking a client through,
            <span className="text-[#2E7FE0]"> unannounced.</span>"
          </blockquote>
          <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#54678A] mt-8">
            — NexGen Sourcing, sourcing policy
          </p>
        </Reveal>
        <div className="lg:col-span-6 space-y-6">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08}>
              <div className="flex gap-6 border-l-2 border-[#2E7FE0]/40 pl-8" data-testid={`pillar-${i}`}>
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <p.icon className="w-5 h-5 text-[#2E7FE0]" />
                    <h3 className="font-display font-semibold text-lg text-[#0A1B33]">{p.title}</h3>
                  </div>
                  <p className="text-[#54678A] text-sm leading-relaxed">{p.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <Reveal>
        <h2 className="font-display font-semibold tracking-tight text-4xl sm:text-5xl leading-[1.05] text-[#0A1B33] mb-4" data-testid="engagement-heading">
          Ways to work with us
        </h2>
        <p className="text-[#54678A] max-w-xl mb-16 leading-relaxed">
          Three engagement models — pick the structure that fits how your business buys.
        </p>
      </Reveal>

      <div className="grid md:grid-cols-3 gap-6">
        {MODELS.map((m, i) => (
          <Reveal key={m.name} delay={i * 0.08} className="h-full">
            <div
              className={`relative rounded-2xl p-10 h-full flex flex-col border transition-colors duration-500 ${
                m.featured
                  ? "bg-[#DCE9FB] border-[#2E7FE0]/60 shadow-[0_0_50px_rgba(46,127,224,0.15)]"
                  : "glass-panel hover:border-[#2E7FE0]/50"
              }`}
              data-testid={`engagement-card-${i}`}
            >
              {m.featured && (
                <span className="absolute -top-3 left-10 font-mono2 text-[10px] uppercase tracking-[0.25em] bg-[#2E7FE0] text-white rounded-full px-4 py-1.5">
                  Most Chosen
                </span>
              )}
              <span className="font-mono2 text-xs text-[#54678A]/60 mb-8">M/{String(i + 1).padStart(2, "0")}</span>
              <h3 className="font-display font-semibold text-2xl text-[#0A1B33] mb-4">{m.name}</h3>
              <p className="text-[#54678A] text-sm leading-relaxed mb-8">{m.desc}</p>
              <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#2E7FE0] mb-4">Best suited for</p>
              <ul className="space-y-3 mb-10 flex-1">
                {m.points.map((pt) => (
                  <li key={pt} className="flex items-start gap-3 text-[#0A1B33] text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#2E7FE0] mt-1.5 shrink-0" />
                    {pt}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => scrollToSection("#contact")}
                className={`inline-flex items-center justify-center gap-2 font-display font-medium text-sm px-6 py-3 rounded-full transition-colors duration-300 ${
                  m.featured
                    ? "bg-[#2E7FE0] hover:bg-[#4a93eb] text-white"
                    : "border border-[#2E7FE0]/40 hover:border-[#2E7FE0] text-[#0A1B33]"
                }`}
                data-testid={`engagement-cta-${i}`}
              >
                Discuss This Model
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
